import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { UiKitModule } from "@jack-sparrow-libraries/ui-kit";
import { MenuProfileComponent } from "@core/layout/components/top-bar/components/menu-profile/menu-profile.component";
import { DojoRoutesEnum, NavigationBase } from "@core/layout/enums/layout-routes.enum";
import { FullClient } from "@models/full-client.model";
import { HasFullAccessDirective } from "@core/directives/has-full-access.directive";
import { HasRestrictedAccessDirective } from "@core/directives/has-restricted-access.directive";
import { NavigationService } from "@services/navigation.service";
import { ClientDroits } from "@jack-sparrow-libraries/emotion-models";
import { animate, style, transition, trigger } from "@angular/animations";
import { EmotionEventsHelperService, StrutsActionType } from "@services/helpers/emotion-events-helper.service";
import { MessageCountFacadeService } from "./services/message-count-facade.service";
import { MessageCountPipe } from "@core/layout/components/top-bar/pipes/message-count.pipe";
import { LayoutService } from "@core/layout/services/layout.service";
import { PatientRoutingService } from "@featuresAY/patients/services/patient-routing.service";

@Component({
	selector: "cba-top-bar",
	templateUrl: "./top-bar.component.html",
	styleUrls: ["./top-bar.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		UiKitModule,
		MenuProfileComponent,
		HasFullAccessDirective,
		HasRestrictedAccessDirective,
		MessageCountPipe,
	],
	animations: [
		trigger("topBarAnimation", [
			transition(":enter", [
				style({
					opacity: 0,
					transform: "translateY(-20px)",
				}),
				animate(
					"200ms cubic-bezier(0.4, 0, 0.2, 1)",
					style({
						opacity: 1,
						transform: "translateY(0)",
					}),
				),
			]),
		]),
	],
})
export class TopBarComponent {
	private readonly navigationService = inject(NavigationService);
	private readonly messageCountFacade = inject(MessageCountFacadeService);
	private readonly layoutService = inject(LayoutService);
	private readonly patientRoutingService = inject(PatientRoutingService);

	readonly $fullClient = input.required<FullClient>({ alias: "fullClient" });
	readonly $loading = input.required<boolean>({ alias: "loading" });
	readonly $droits = input.required<ClientDroits>({ alias: "droits" });

	readonly $messageCount = this.messageCountFacade.$messageCount;

	readonly $smallMessageCountText = computed(() => this.$messageCount() >= MessageCountPipe.MESSAGE_COUNT_THRESHOLD);

	goToDashboard(): void {
		this.navigationService.navigateByBase(NavigationBase.DOJO, DojoRoutesEnum.DASHBOARD);
	}

	async openSearchDialog(): Promise<void> {
		const patientId = await this.layoutService.selectPatient();
		if (patientId) {
			await this.patientRoutingService.routeToDetail(patientId);
		}
	}

	openNotificationsDialog(): void {
		EmotionEventsHelperService.dispatchEvent({
			action: StrutsActionType.OPEN_NOTIFICATIONS_DIALOG,
			payload: null,
		});
	}
}
